<template>
  <div class="relation-list-line" :class="{ '-editable': type !== 'outgoing' && editable }">
    <button-item
      :selected="selected"
      :icons="deletable ? ['trash-2'] : []"
      :class="{ '-hidden': hidden }"
      class="relation-list-line__button"
      @click:trash-2="removeRelation(relation)"
      @pointerenter="highlight(relation)"
      @pointerleave="removeHighlight(relation)"
      @click.prevent="selectRelation(relation)"
    >
      <span v-if="item?.title">
        {{ item.title }}
      </span>
      <span v-else class="placeholder-text">
        {{ $t("labels.unnamed") }}
      </span>
    </button-item>

    <div class="button-item button-item_light p-0 justify-items-center items-center">
      <lucide-icon v-if="icon" :name="icon" class="relation-list-line__icon" />
    </div>

    <input-number
      v-if="type !== 'outgoing' && editable"
      :value="relation.maxInfluence"
      :min="0"
      :max="null"
      step="any"
      @update:value="updateInfluence(relation, $event)"
    />
    <span v-else class="relation-list-line__value">{{ $n(relation.maxInfluence, {
      notation: "compact"
    }) }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";

import ButtonItem from "@core/input/ButtonItem.vue";
import InputNumber from "@core/input/InputNumber.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";

import type { RelationItem } from "./RelationItem";
import { useSceneRelationSettingsStore } from "./sceneRelationSettings.store";
import { useSceneTree } from "../scene/sceneTree";

const props = withDefaults(defineProps<{
  type: "incoming" | "outgoing";
  relation: RelationItem;
  editable?: boolean;
  selected?: boolean;
  deletable?: boolean;
}>(), {
  editable: true,
  deletable: false,
  selected: false
});

const emit = defineEmits<{
  (event: "select", relation: RelationItem | null): void;
}>();

const item = computed(() => {
  if (props.type === "incoming") {
    if (props.relation.startItemType === "target") {
      return useTargetStore().items[props.relation.startItemId];
    } else if (props.relation.startItemType === "resource") {
      return useResourceStore().items[props.relation.startItemId];
    } else if (props.relation.startItemType === "index") {
      return useIndexStore().items[props.relation.startItemId];
    }
  }
  if (props.type === "outgoing") {
    if (props.relation.endItemType === "target") {
      return useTargetStore().items[props.relation.endItemId];
    } else {
      return useResourceStore().items[props.relation.endItemId];
    }
  }
  return null;
});

const icon = computed(() => {
  if (props.relation === null) return null;
  if (props.relation.updateType == "none") return "x";
  if (props.relation.inverse) return "inverse";
  return "not-inverse";
});

const sceneTree = useSceneTree();

const hidden = computed(() => {
  return useSceneRelationSettingsStore().byScene[sceneTree.activeScene.id]?.[props.relation.id]?.visible === false;
});

function updateInfluence(relation: RelationItem, value: number) {
  useRelationStore().update({
    id: relation.id,
    maxInfluence: value
  });
}
function highlight(item: RelationItem) {
  useSelectionStore().setHighlight(item);
}
function removeHighlight(item: RelationItem) {
  useSelectionStore().removeHighlight(item);
}
function selectRelation(relation: RelationItem) {
  removeHighlight(relation);
  emit("select", relation);
}
function removeRelation(relation: RelationItem) {
  emit("select", null);
  useRelationStore().delete(relation);
}
</script>

<style lang="scss">
.relation-list-line {
  display: grid;
  align-items: center;

  &.-editable {
    padding-right: 0;
  }
  &__button {
    &.-hidden {
      opacity: 0.5;
      border: 1px dashed var(--color-text-medium);
      box-shadow: none;

      &:hover {
        opacity: 0.8;
        box-shadow: 0 2px 3px var(--color-shadow-3);
      }
    }
  }
  &__icon {
    font-size: 14px;
    padding: 0;
    align-items: center;
    justify-content: center;
    justify-items: center;
  }
  &__value {
    min-width: 2.75rem;
    text-align: right;
  }
}
</style>
